import React, { useState, useEffect } from 'react';
import { LocalizationProvider, TimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import Helper from "../helpers";
import config from '../config';
import LayoutWrapper from '../components/LayoutWrapper';

const baseUrl = config.apiBaseUrl;
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const BulkSession = () => {
    const [selectedDays, setSelectedDays] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [sessionTimes, setSessionTimes] = useState([dayjs().add(1, 'hour')]);
    const [sessionLength, setSessionLength] = useState(1);
    const [sessionSeats, setSessionSeats] = useState(1);
    const [sessionTokenCost, setSessionTokenCost] = useState(1);
    const [startDate, setStartDate] = useState(dayjs());
    const [numWeeks, setNumWeeks] = useState(1);
    const [sessionName, setSessionName] = useState("");
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [currentVenueKey, setCurrentVenueKey] = useState("");

    useEffect(() => {
        Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
    }, []);

    const handleDayChange = (day) => {
        setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
    };

    const handleAddSession = () => {
        if (sessions.length >= 1000) {
            alert('You can only add up to 1000 sessions at a time.');
            return;
        }

        const newSessions = [];
        for (let i = 0; i < numWeeks; i++) {
            selectedDays.forEach(day => {
                sessionTimes.forEach(time => {
                    const sessionDate = startDate.add(i, 'week').day(daysOfWeek.indexOf(day) + 1);
                    newSessions.push({
                        day,
                        date: sessionDate,
                        time,
                        length: sessionLength,
                        seats: sessionSeats,
                        tokenCost: sessionTokenCost,
                        name: sessionName,
                    });
                });
            });
        }

        setSessions([...sessions, ...newSessions]);
    };

    const handleRemoveSession = (index) => {
        const updatedSessions = sessions.filter((_, i) => i !== index);
        setSessions(updatedSessions);
    };

    const handleAddTime = () => {
        setSessionTimes([...sessionTimes, dayjs().add(1, 'hour')]);
    };

    const handleRemoveTime = (index) => {
        const updatedTimes = sessionTimes.filter((_, i) => i !== index);
        setSessionTimes(updatedTimes);
    };

    const handleClose = () => {
        setSessions([]);
        setSessionTimes([dayjs().add(1, 'hour')]);
        setSessionLength(1);
        setSessionSeats(1);
        setSessionTokenCost(1);
        setStartDate(dayjs());
        setNumWeeks(1);
        setSessionName("");
    };
    const handleSubmit = async () => {
        const sessionData =  sessions.map(session => ({
            // No trailing Z: this is a local wall-clock time, not UTC (see BulkClass.js for
            // the full explanation of the display-time drift this used to cause).
            startDate: `${session.date.format('YYYY-MM-DD')}T${session.time.format('HH:mm:00')}`,
            availableSeats: session.seats,
            numberOfMinuets: session.length * 60,
            name: session.name,
            tokenCost: session.tokenCost,
        }));
        const token = await Helper.getCookie('wh-jwt');
//AddSession
        fetch(`${baseUrl}/api/Sessions/AddSession/${currentVenueKey}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(sessionData),
        })
            .then(response => {
                if (response.ok) {
                    alert('Sessions added successfully');
                    handleClose();
                } else {
                    alert('Failed to add sessions');
                }
            })
            .catch(error => {
                console.error('Error adding sessions', error);
                alert('Failed to add sessions');
            });
    };

    return (
        <LayoutWrapper 
            title="Bulk Add Sessions"
            subtitle="Create multiple sessions across weeks with flexible scheduling"
        >
            <div className="space-y-4 sm:space-y-6">
                {/* Venue Selection */}
                {venueKeyOptions.length > 0 && (
                    <div className="card p-4 sm:p-6">
                        <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Select Venue</h3>
                        <select
                            value={currentVenueKey}
                            onChange={(e) => setCurrentVenueKey(e.target.value)}
                            className="input-field"
                        >
                            {venueKeyOptions.map((venueKey) => (
                                <option key={venueKey} value={venueKey}>
                                    {venueKey}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Day Selection */}
                <div className="card p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Select Days</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
                        {daysOfWeek.map(day => (
                            <label key={day} className="flex items-center space-x-2 cursor-pointer p-2 rounded-lg hover:bg-gray-800 transition-colors">
                                <input
                                    type="checkbox"
                                    checked={selectedDays.includes(day)}
                                    onChange={() => handleDayChange(day)}
                                    className="w-4 h-4 text-gray-600 bg-gray-800 border-gray-600 rounded focus:ring-gray-500"
                                />
                                <span className="text-xs sm:text-sm text-gray-300">{day}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Date and Time Configuration */}
                <div className="card p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Date & Time Configuration</h3>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Start Date
                                </label>
                                <DatePicker
                                    value={startDate}
                                    format="DD/MM/YYYY"
                                    onChange={(newValue) => setStartDate(newValue)}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined"
                                        }
                                    }}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Number of Weeks
                                </label>
                                <input
                                    type="number"
                                    value={numWeeks}
                                    onChange={(e) => setNumWeeks(e.target.value)}
                                    className="input-field"
                                    min="1"
                                />
                            </div>
                        </div>

                        <div className="mt-4 sm:mt-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-2">
                                <label className="text-sm font-medium text-gray-300">
                                    Session Times
                                </label>
                                <button
                                    onClick={handleAddTime}
                                    className="btn-outline flex items-center justify-center w-full sm:w-auto"
                                >
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Add Time
                                </button>
                            </div>
                            <div className="space-y-3">
                                {sessionTimes.map((time, index) => (
                                    <div key={index} className="flex items-center space-x-3">
                                        <div className="flex-1">
                                            <TimePicker
                                                value={time}
                                                onChange={(newValue) => {
                                                    const updatedTimes = [...sessionTimes];
                                                    updatedTimes[index] = newValue;
                                                    setSessionTimes(updatedTimes);
                                                }}
                                                ampm={false}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        variant: "outlined"
                                                    }
                                                }}
                                            />
                                        </div>
                                        {sessionTimes.length > 1 && (
                                            <button
                                                onClick={() => handleRemoveTime(index)}
                                                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </LocalizationProvider>
                </div>

                {/* Session Details */}
                <div className="card p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Session Details</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Session Name
                            </label>
                            <input
                                type="text"
                                value={sessionName}
                                onChange={(e) => setSessionName(e.target.value)}
                                className="input-field"
                                placeholder="Enter session name"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Session Length (hours)
                                </label>
                                <input
                                    type="number"
                                    value={sessionLength}
                                    onChange={(e) => setSessionLength(e.target.value)}
                                    className="input-field"
                                    min="1"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Available Seats
                                </label>
                                <input
                                    type="number"
                                    value={sessionSeats}
                                    onChange={(e) => setSessionSeats(e.target.value)}
                                    className="input-field"
                                    min="1"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Token Cost
                                </label>
                                <input
                                    type="number"
                                    value={sessionTokenCost}
                                    onChange={(e) => setSessionTokenCost(e.target.value)}
                                    className="input-field"
                                    min="1"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                    <button
                        onClick={handleAddSession}
                        className="btn-primary flex items-center justify-center w-full sm:w-auto"
                    >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Plan Sessions
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center w-full sm:w-auto"
                    >
                        Submit All Sessions
                    </button>
                </div>

                {/* Summary */}
                <div className="card p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-center sm:text-left">
                            <p className="text-sm text-gray-300">Number of Sessions</p>
                            <p className="text-xl sm:text-2xl font-bold text-white">{sessions.length}</p>
                        </div>
                        <div className="text-center sm:text-left">
                            <p className="text-sm text-gray-300">Start Times</p>
                            <p className="text-base sm:text-lg text-white">
                                {sessionTimes.map(time => time.format('HH:mm')).join(', ')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Sessions Table/Cards */}
                {sessions.length > 0 && (
                    <>
                        {/* Desktop Table */}
                        <div className="hidden lg:block card overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-700">
                                <h3 className="text-lg font-semibold text-white">Planned Sessions</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-700">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="px-6 py-3 text-left">Day</th>
                                            <th className="px-6 py-3 text-left">Date</th>
                                            <th className="px-6 py-3 text-center">Time</th>
                                            <th className="px-6 py-3 text-center">Length</th>
                                            <th className="px-6 py-3 text-center">Seats</th>
                                            <th className="px-6 py-3 text-center">Token Cost</th>
                                            <th className="px-6 py-3 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-gray-900 divide-y divide-gray-700">
                                        {sessions.map((session, index) => (
                                            <tr key={index} className="hover:bg-gray-800 transition-colors">
                                                <td className="table-cell">
                                                    <span className="text-white">{session.day}</span>
                                                </td>
                                                <td className="table-cell">
                                                    <span className="text-white">{session.date.format('YYYY-MM-DD')}</span>
                                                </td>
                                                <td className="table-cell text-center">
                                                    <span className="text-white">{session.time.format('HH:mm')}</span>
                                                </td>
                                                <td className="table-cell text-center">
                                                    <span className="text-white">{session.length}h</span>
                                                </td>
                                                <td className="table-cell text-center">
                                                    <span className="text-white">{session.seats}</span>
                                                </td>
                                                <td className="table-cell text-center">
                                                    <span className="text-white">{session.tokenCost}</span>
                                                </td>
                                                <td className="table-cell text-center">
                                                    <button
                                                        onClick={() => handleRemoveSession(index)}
                                                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile Cards */}
                        <div className="lg:hidden space-y-3">
                            <div className="card p-4">
                                <h3 className="text-lg font-semibold text-white mb-4">Planned Sessions ({sessions.length})</h3>
                            </div>
                            {sessions.map((session, index) => (
                                <div key={index} className="card p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="text-sm font-medium text-white">{session.name}</div>
                                            <div className="text-xs text-gray-300">{session.day}</div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveSession(index)}
                                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="text-gray-300">Date:</span>
                                            <span className="text-white ml-1">{session.date.format('YYYY-MM-DD')}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-300">Time:</span>
                                            <span className="text-white ml-1">{session.time.format('HH:mm')}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-300">Length:</span>
                                            <span className="text-white ml-1">{session.length}h</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-300">Seats:</span>
                                            <span className="text-white ml-1">{session.seats}</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-gray-300">Token Cost:</span>
                                            <span className="text-white ml-1">{session.tokenCost}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </LayoutWrapper>
    );
};

export default BulkSession;
